import { useRef, useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { notificationSettingsApi } from "@/api/notificationSettings";
import { useIsMobile } from "@/hooks/use-mobile";

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function NotificationBell() {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const queryClient = useQueryClient();
    const isMobile = useIsMobile();

    const { data: feed = [] } = useQuery(notificationSettingsApi.getFeedQueryOptions());
    const unreadCount = feed.filter((n) => !n.isRead).length;

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const handleMarkRead = async (id: number) => {
        try {
            await notificationSettingsApi.markRead(id);
            queryClient.setQueryData<typeof feed>(["notification-feed"], (prev) =>
                prev?.map((n) => (n.id === id ? { ...n, isRead: true } : n))
            );
        } catch {
            toast.error("Failed to mark notification as read");
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await notificationSettingsApi.markAllRead();
            queryClient.setQueryData<typeof feed>(["notification-feed"], (prev) =>
                prev?.map((n) => ({ ...n, isRead: true }))
            );
        } catch {
            toast.error("Failed to mark all notifications as read");
        }
    };

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen((v) => !v)}
                className="relative p-2 rounded-full text-blue-200 hover:bg-white/10 hover:text-white transition-colors cursor-pointer"
                title="Notifications"
            >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-0.5 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center leading-none">
                        {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className={`z-50 rounded-md border border-outline-variant/20 bg-surface-container-lowest shadow-lg overflow-hidden ${isMobile ? "fixed left-2 right-2 top-[4.5rem]" : "absolute top-full mt-2 right-0 w-80"}`}
                >
                    <div className="px-3 py-2 border-b border-outline-variant/20 flex items-center justify-between">
                        <span className="text-sm font-semibold text-on-surface">Notifications</span>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                className="text-xs text-primary hover:underline cursor-pointer"
                            >
                                Mark all as read
                            </button>
                        )}
                    </div>

                    {feed.length === 0 ? (
                        <div className="px-3 py-6 text-center text-sm text-on-surface-variant">
                            No recent notifications
                        </div>
                    ) : (
                        <ul className="max-h-96 overflow-y-auto divide-y divide-outline-variant/10">
                            {feed.map((item) => (
                                <li
                                    key={item.id}
                                    onClick={() => !item.isRead && handleMarkRead(item.id)}
                                    className={`px-3 py-2.5 transition-colors ${
                                        item.isRead
                                            ? "hover:bg-surface-container"
                                            : "bg-primary/5 hover:bg-primary/10 cursor-pointer"
                                    }`}
                                >
                                    <div className="flex items-start gap-2">
                                        {!item.isRead && (
                                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                                        )}
                                        <div className={!item.isRead ? "" : "pl-3.5"}>
                                            <p className="text-xs font-medium text-on-surface">{item.title}</p>
                                            <p className="text-xs text-on-surface-variant mt-0.5">
                                                {item.body}
                                                <span className="ml-1.5 text-on-surface-variant/60">
                                                    · {formatDate(item.createdAt)}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
